/**
 * AboutPage — The story behind TableTryb.
 * Public-facing, no authentication required.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import '../../styles/AboutPage.css';

const AboutPage: React.FC = () => {
  return (
    <div className="about-page">
      {/* ============================================================ */}
      {/* HERO SECTION                                                  */}
      {/* ============================================================ */}
      <section className="about-hero">
        <p className="about-eyebrow">Who We Are</p>
        <h1 className="about-title">
          The Story Behind <span className="hero-highlight">TableTryb</span>
        </h1>
        <p className="about-lead">
          TableTryb started the way a lot of good ideas do — with a problem nobody had time to solve.
        </p>
      </section>

      {/* ============================================================ */}
      {/* STORY SECTION                                                 */}
      {/* ============================================================ */}
      <section className="about-story">
        <div className="about-prose">
          <p>
            Between kids' activities, full-throttle careers, and the daily chaos of family life,
            my wife and I found ourselves stuck in the same cycle: too tired to plan, too busy to
            shop, too guilty about the takeout. Again. Another pizza. Another drive-through.
            Another night where we meant to cook but didn't.
          </p>
          <p>
            What we really wanted was to recapture something from our own childhoods — those family
            dinners around the table. But the reality of modern life kept getting in the way.
            Meal planning is work. You have to decide what to make, list out every ingredient,
            and carve out time to go shopping. Multiply that by a full week and it starts to feel
            like a part-time job.
          </p>
          <p>
            With a career in the tech industry, my wife and I naturally looked to technology to solve
            our problem. So we started building — nights and weekends, piece by piece — an app to
            automate this part of our lives.
          </p>
          <p>
            The first feature we tackled was AI recipe import. We had a stack of recipes stuffed in
            our kitchen cabinet — cards, magazine clippings, printouts from years of cooking.
            Rather than retype them all, we built a tool to capture them with a photo from a phone
            and pull out the ingredients and steps automatically. Snap, extract, done.
          </p>
          <p>
            Then we made it a family affair. We created accounts for our kids so they could vote on
            the meals they wanted for the week. My wife or I would review the votes, finalize the
            meal plan, and the app would generate the grocery list automatically. We even added a
            quick search feature at our favorite grocery store, H-E-B, to speed up the shopping.
          </p>

          <blockquote className="about-pullquote">
            What started as a side project for our family changed how we eat together.
          </blockquote>

          <p>
            Realizing this solution could help a million families just like ours, TableTryb was born.
          </p>
          <p>
            Come join us. Automate one of the most tedious parts of feeding a family. Spend less time
            planning and more time together — around the table, not at a drive-through.
          </p>
        </div>
      </section>

      {/* ============================================================ */}
      {/* FINAL CTA                                                     */}
      {/* ============================================================ */}
      <section className="final-cta">
        <h2>Feed your tribe. 🍽️</h2>
        <p>Start your 14-day free trial. No credit card required.</p>
        <Link to="/signup" className="btn btn-primary btn-lg">
          Start Your Free Trial
          <ArrowRight size={20} />
        </Link>
      </section>
    </div>
  );
};

export default AboutPage;